import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Ellipsis, LogOut, Settings, Smartphone, type LucideIcon } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useOpenDaily } from "@/book/components/daily";
import { useBookTracking } from "@/book/tracking";
import { FunctionTile, SoonTile, TILE_CLASS, TileGrid } from "@/book/components/function-tile";
import { ADMIN, isDaily, isMoreActive, MORE, PRIMARY, PROFILE, SOON } from "@/book/nav";
import { useLogout, useMe } from "@/shared/api/auth";
import { cn, monogram } from "@/shared/lib/utils";
import { track } from "@/shared/lib/track";
import { setViewMode } from "@/shared/lib/view-mode";
import { Brand } from "@/shared/ui/mark";
import { SectionLabel } from "@/shared/ui/section-label";
import { Sheet } from "@/shared/ui/sheet";

type Daily = ReturnType<typeof useOpenDaily>;

/** Melléklet-váz (design-rendszer 6.): mobilon és asztalon ugyanaz a szerkezet — a négy fő
 *  fül + „Több”. Mobilon fejléc (jel · avatar) + tartalom + alsó sáv, a „Több” alulról
 *  felcsúszó panel; asztalon felső sáv (jel · fülek · avatar a profil-menüvel), a „Több”
 *  lenyíló menü, a tartalom középre zárt hasáb. */
export function BookShell() {
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  const { pathname } = useLocation();
  // a lekérdezés itt fut, nem a csak nyitva élő „Több” tartalmában: így a csempe azonnal kész
  const daily = useOpenDaily();
  useBookTracking();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreCurrent = isMoreActive(pathname);
  const moreActive = moreOpen || moreCurrent;

  return (
    <div className="flex min-h-(--app-h) flex-col">
      <TopBar isAdmin={isAdmin} moreCurrent={moreCurrent} daily={daily} />

      <header className="mx-auto flex w-full max-w-screen-md items-center justify-between px-5 pb-2.5 pt-[18px] md:hidden">
        <Brand />
        <Link to={PROFILE.to} aria-label="Profil" className="flex">
          <Avatar name={me?.display_name} />
        </Link>
      </header>

      <main className="mx-auto w-full max-w-screen-md flex-1 px-5 pb-24 pt-1.5 md:max-w-[784px] md:px-8 md:pb-10 md:pt-8">
        <Outlet />
      </main>

      <nav
        aria-label="Fő navigáció"
        className="fixed inset-x-0 bottom-0 z-20 flex border-t border-line bg-white px-1.5 pt-2 pb-[calc(16px+env(safe-area-inset-bottom))] md:hidden"
      >
        {PRIMARY.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => tabClass(isActive)}>
            {({ isActive }) => (
              <>
                <Icon size={22} strokeWidth={isActive ? 2 : 1.8} />
                {label}
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-current={moreCurrent ? "page" : undefined}
          onClick={() => setMoreOpen(true)}
          className={tabClass(moreActive)}
        >
          <Ellipsis size={22} strokeWidth={moreActive ? 2 : 1.8} />
          Több
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen} title="Több">
        <MoreContent isAdmin={isAdmin} daily={daily} onNavigate={() => setMoreOpen(false)} />
      </Sheet>
    </div>
  );
}

function Avatar({ name, size = 36 }: { name?: string; size?: 36 | 40 }) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-gold-soft font-extrabold text-ink",
        size === 36 ? "h-9 w-9 text-[14px]" : "h-10 w-10 text-[15px]",
      )}
    >
      {name ? monogram(name) : ""}
    </span>
  );
}

/** Alsó sáv füle: ikon 22 px + felirat 11 px; aktív `ink` / 600, inaktív `muted` / 500. */
function tabClass(active: boolean): string {
  return cn(
    "flex min-h-11 flex-1 flex-col items-center gap-1 py-1.5 text-center text-[11px] leading-tight",
    active ? "font-semibold text-ink" : "font-medium text-muted",
  );
}

/** A „Több” tartalma — mobilon az alsó panelben, asztalon a lenyíló menüben ugyanez: a négy
 *  fő fülön kívüli funkciók csempéi → „AI” szakasz a hamarosan érkező pontokkal → adminnak
 *  utolsóként az Admin. Választáskor navigál és bezár. */
function MoreContent({
  isAdmin,
  daily,
  onNavigate,
}: {
  isAdmin: boolean;
  daily: Daily;
  onNavigate: () => void;
}) {
  return (
    <div className="flex flex-col gap-[18px]">
      <TileGrid>
        {MORE.map((entry) =>
          isDaily(entry) ? (
            <button
              key="daily"
              type="button"
              disabled={daily.disabled}
              onClick={() => {
                onNavigate();
                daily.open();
              }}
              className={cn(TILE_CLASS, "hover:bg-paper disabled:opacity-60")}
            >
              <entry.icon size={22} strokeWidth={1.8} className="text-gold" />
              <span className="text-[13px] font-semibold leading-tight">{entry.label}</span>
            </button>
          ) : (
            <FunctionTile key={entry.to} {...entry} onClick={onNavigate} />
          ),
        )}
      </TileGrid>
      <div className="flex flex-col gap-2">
        <SectionLabel>AI</SectionLabel>
        <TileGrid>
          {SOON.map((item) => (
            <SoonTile key={item.label} {...item} />
          ))}
        </TileGrid>
      </div>
      {isAdmin && (
        <TileGrid>
          <FunctionTile {...ADMIN} onClick={onNavigate} />
        </TileGrid>
      )}
    </div>
  );
}

/** Lenyíló panel (asztal): a „Több” és a profil-menü közös kerete. */
const PANEL_CLASS = "z-40 rounded-lg border border-line bg-white shadow-menu";

/** Felső sáv (asztal): balra a jel, középen a négy fő fül + „Több”, jobbra az avatar. A
 *  mobilnézet keretében (és keskeny ablakban) rejtve — ott az alsó sáv szól. */
function TopBar({
  isAdmin,
  moreCurrent,
  daily,
}: {
  isAdmin: boolean;
  moreCurrent: boolean;
  daily: Daily;
}) {
  return (
    <header className="hidden border-b border-line bg-white md:block">
      <div className="mx-auto flex min-h-[68px] max-w-[1104px] flex-wrap items-center gap-x-6 gap-y-2 px-8 py-3">
        <Brand />
        <nav aria-label="Fő navigáció" className="flex flex-1 flex-wrap justify-center gap-1">
          {PRIMARY.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => topTabClass(isActive)}>
              <Icon size={20} strokeWidth={1.8} />
              {label}
            </NavLink>
          ))}
          <MoreMenu isAdmin={isAdmin} current={moreCurrent} daily={daily} />
        </nav>
        <ProfileMenu isAdmin={isAdmin} />
      </div>
    </header>
  );
}

/** Felső sáv füle: 40 px, ikon 20 px + felirat 14 px; aktív `gold-soft` háttér + `ink` / 600,
 *  inaktív `muted` / 500 (a korábbi oldalsáv-sor stílusa, vízszintesen). */
function topTabClass(active: boolean, open = false): string {
  return cn(
    "flex h-10 items-center gap-2 whitespace-nowrap rounded-md px-3.5 text-[14px]",
    active ? "bg-gold-soft font-semibold text-ink" : "font-medium text-muted hover:bg-paper",
    open && !active && "bg-gray text-ink",
  );
}

function MoreMenu({ isAdmin, current, daily }: { isAdmin: boolean; current: boolean; daily: Daily }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        aria-current={current ? "page" : undefined}
        className={cn(topTabClass(current, open), "pr-3")}
      >
        <Ellipsis size={20} strokeWidth={1.8} />
        Több
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={cn("transition-transform", open && "rotate-180")}
        />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content aria-label="Több" sideOffset={10} className={cn(PANEL_CLASS, "w-[420px] p-4")}>
          <MoreContent isAdmin={isAdmin} daily={daily} onNavigate={() => setOpen(false)} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const MENU_ROW_CLASS =
  "flex min-h-11 w-full items-center gap-3 rounded-sm px-3 text-left text-[14px] font-medium text-ink hover:bg-paper";

function MenuLink({
  to,
  icon: Icon,
  onClick,
  children,
}: {
  to: string;
  icon: LucideIcon;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Link to={to} onClick={onClick} className={MENU_ROW_CLASS}>
      <Icon size={18} strokeWidth={1.8} className="shrink-0 text-muted" />
      {children}
    </Link>
  );
}

const Divider = () => <div className="mx-1 my-1.5 h-px bg-line" />;

/** Profil-menü (asztal, jobb felső sarok): Profil, fiók → Mobilnézet → Admin, Kilépek. */
function ProfileMenu({ isAdmin }: { isAdmin: boolean }) {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const switchId = useId();
  const close = () => setOpen(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        aria-label="Profil-menü"
        className={cn(
          "flex h-11 items-center gap-1.5 rounded-full py-1 pl-1 pr-2 text-muted hover:bg-paper",
          open && "bg-gray",
        )}
      >
        <Avatar name={me?.display_name} />
        <ChevronDown size={16} strokeWidth={2} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          aria-label="Profil-menü"
          align="end"
          sideOffset={8}
          className={cn(PANEL_CLASS, "flex w-[320px] flex-col gap-0.5 p-2")}
        >
          <div className="flex items-center gap-3 px-3 pb-3 pt-2.5">
            <Avatar name={me?.display_name} size={40} />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-[15px] font-semibold">{me?.display_name}</span>
              <span className="truncate text-[12px] text-muted">{me?.email}</span>
            </span>
          </div>
          <MenuLink to={PROFILE.to} icon={PROFILE.icon} onClick={close}>
            Profil
          </MenuLink>
          <MenuLink to="/app/profil" icon={Settings} onClick={close}>
            Fiók, hírlevél, alkalmazásaim
          </MenuLink>
          <Divider />
          {/* Asztali nézetben mindig „ki” állású: bekapcsolva a keret jön, és a menü eltűnik;
              visszakapcsolni a keret melletti „Asztali nézet” gombbal lehet. */}
          <button
            type="button"
            role="switch"
            aria-checked={false}
            aria-labelledby={`${switchId}-label`}
            aria-describedby={`${switchId}-hint`}
            onClick={() => {
              close();
              setViewMode("mobile");
              track("core.view_mode", "mobile");
            }}
            className="flex flex-col gap-1 rounded-sm bg-paper px-3 py-2.5 text-left hover:bg-gold-soft"
          >
            <span className="flex w-full items-center gap-3">
              <Smartphone size={18} strokeWidth={1.8} className="shrink-0 text-gold" />
              <span id={`${switchId}-label`} className="flex-1 text-[14px] font-semibold">
                Mobilnézet
              </span>
              <span aria-hidden="true" className="relative h-6 w-10 shrink-0 rounded-full bg-line">
                <span className="absolute left-[3px] top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-segment" />
              </span>
            </span>
            <span id={`${switchId}-hint`} className="pl-[30px] text-[12px] leading-[1.4] text-muted">
              Telefonos elrendezés középre igazítva — például kivetítéshez. Ezen a gépen megmarad.
            </span>
          </button>
          <Divider />
          {isAdmin && (
            <MenuLink to={ADMIN.to} icon={ADMIN.icon} onClick={close}>
              Admin
            </MenuLink>
          )}
          <button
            type="button"
            disabled={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => navigate("/app/belepes", { replace: true }),
              })
            }
            className={cn(MENU_ROW_CLASS, "disabled:opacity-50")}
          >
            <LogOut size={18} strokeWidth={1.8} className="shrink-0 text-muted" />
            Kilépek
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
